import { CompleteCheckoutIO } from '../types';
import {
  UseJustfiIntegration1MutationEndpointArgs,
  useJustfiIntegration1Mutation,
} from './use-justfi-integration1-mutation';

type MutationContext<C = unknown> = {
  // Add internal mutation context here if needed (useful for optimistic updates)
  otherContext?: C;
};

/**
 * A hook that returns a mutation for the `CompleteCheckout` mutation endpoint.
 * It handles query invalidation for the relevant query endpoints internally.
 * @param options (optional) The options to pass to `useMutation`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useCompleteCheckoutMutation = <
  E = unknown,
  C = unknown,
  OtherOptions extends object = never,
  RequestOverride extends CompleteCheckoutIO['input'] = CompleteCheckoutIO['input']
>({
  options,
  ...args
}: UseJustfiIntegration1MutationEndpointArgs<
  'CompleteCheckout',
  E,
  C | undefined,
  OtherOptions,
  RequestOverride
> = {}) =>
  useJustfiIntegration1Mutation<'CompleteCheckout', E, MutationContext<C>, OtherOptions, RequestOverride>({
    endpointName: 'CompleteCheckout',
    ...args,
    options: {
      ...options,
      onMutate: async (request) => {
        // Implement optimistic updates here

        return {
          // Add custom context type here (if exists in `MutationContext`)
          otherContext: await options?.onMutate?.(request),
        };
      },
      onSuccess: (response, request, context) => {
        // Implement query updates or invalidation for affected queries here

        return options?.onSuccess?.(response, request, context?.otherContext);
      },
      onError: (error, request, context) => {
        // Revert optimistic update here (if implemented)

        return options?.onError?.(error, request, context?.otherContext);
      },
      onSettled: (response, error, request, context) => {
        // Only pass context of type C into provided `onSettled` option
        return options?.onSettled?.(response, error, request, context?.otherContext);
      },
    },
  });
